import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../supabase";

export default function ChoirGames() {
  const [songs, setSongs] = useState([]);
  const [stage, setStage] = useState(0);
  const [userGuess, setUserGuess] = useState("");
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [options, setOptions] = useState([]);
  const audioRef = useRef(null);
  const [audioTime, setAudioTime] = useState(0);

  // Fetch songs with audio
  useEffect(() => {
    const fetchSongs = async () => {
      const { data } = await supabase
        .from("songs")
        .select("id, title, composer, audio_url")
        .eq("is_public", true)
        .neq("audio_url", "")
        .neq("audio_url", null);
      if (data && data.length > 0) {
        // Shuffle and select up to 10 for the game
        const shuffledSongs = data
          .sort(() => 0.5 - Math.random())
          .slice(0, Math.min(10, data.length));
        setSongs(shuffledSongs);
      }
    };
    fetchSongs();
  }, []);

  // Reset game state when new songs loaded
  useEffect(() => {
    setStage(0);
    setScore(0);
    setUserGuess("");
    setGameOver(false);
    setShowResult(false);
    setShowAnswer(false);
    setAudioTime(0);
  }, [songs]);

  // Update options whenever stage changes
  useEffect(() => {
    if (songs.length && songs[stage]) {
      const correct = songs[stage].title;
      const otherTitles = songs
        .filter((s, i) => i !== stage)
        .map((s) => s.title);
      const wrongOptions = [];
      while (wrongOptions.length < 3 && otherTitles.length) {
        const idx = Math.floor(Math.random() * otherTitles.length);
        if (!wrongOptions.includes(otherTitles[idx])) {
          wrongOptions.push(otherTitles[idx]);
        }
      }
      const allOptions = [correct, ...wrongOptions].sort(() => 0.5 - Math.random());
      setOptions(allOptions);
      setUserGuess("");
    }
  }, [stage, songs]);

  // Handle preview end (auto stop after 45s)
  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      if (audioRef.current) {
        setAudioTime(audioRef.current.currentTime);
        if (audioRef.current.currentTime >= 45) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      }
    }, 200);
    return () => clearInterval(timer);
  }, [isPlaying, stage]);

  // Start 45s preview when stage loads
  useEffect(() => {
    setShowResult(false);
    setShowAnswer(false);
    setUserGuess("");
    setAudioTime(0);
    setIsPlaying(false); // Reset play button every stage
    if (songs[stage] && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.pause();
    }
  }, [stage, songs]);

  const handlePlayPreview = () => {
    setIsPlaying(true);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
  };

  const handleGuess = (e) => {
    e.preventDefault();
    if (!songs[stage]) return;
    setShowResult(true);
    setShowAnswer(true);
    const correct = userGuess.trim().toLowerCase() === songs[stage].title.trim().toLowerCase();
    if (correct) setScore(score + 1);
    setTimeout(() => {
      if (stage + 1 < songs.length) {
        setShowResult(false);
        setShowAnswer(false);
        setStage(s => s + 1);
      } else {
        setGameOver(true);
        setShowResult(false);
        setShowAnswer(false);
      }
    }, 1500);
  };

  const handleRestart = () => {
    setStage(0);
    setScore(0);
    setGameOver(false);
    setUserGuess("");
    setShowResult(false);
    setShowAnswer(false);
    setAudioTime(0);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.pause();
    }
    // Shuffle again
    setSongs((prev) => prev.sort(() => 0.5 - Math.random()));
  };

  // Share helpers
  const siteUrl = "https://choircenter.com/games";
  const shareText = encodeURIComponent(
    score === songs.length
      ? `🎉 I scored 100% on the Choir Song Game at ChoirCenter.com! Try to beat me:`
      : `I scored ${score} out of ${songs.length} on the Choir Song Game at ChoirCenter.com. Play here:`
  );
  const fbShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}&quote=${shareText}`;
  const waShare = `https://wa.me/?text=${shareText} ${encodeURIComponent(siteUrl)}`;
  const xShare = `https://x.com/intent/tweet?text=${shareText} ${encodeURIComponent(siteUrl)}`;

  return (
    <div className="min-h-[75vh] py-10 px-2 bg-gradient-to-tr from-lime-50 to-green-50 flex flex-col items-center">
      <div className="w-full max-w-2xl mx-auto rounded-3xl bg-white shadow-xl p-8 flex flex-col items-center border border-green-100">
        <div className="flex flex-col items-center gap-1 mb-3">
          <div className="bg-green-100 p-3 rounded-full mb-2 shadow">
            <svg className="w-10 h-10 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-2v13" />
              <circle cx="6" cy="18" r="3" strokeWidth="1.5" />
              <circle cx="18" cy="17" r="3" strokeWidth="1.5" />
            </svg>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-green-800">🎵 Guess the Choir Song!</h1>
          <p className="text-green-700 text-base max-w-lg text-center">
            Listen to a 45-second choir song preview, then select the correct title. Complete all stages to become a Choir Center Game Master!
          </p>
        </div>
        <div className="w-full my-5">
          <div className="flex justify-between items-center w-full mb-3">
            <div className="text-green-800 font-bold text-xl">
              Stage <span className="text-lime-600">{gameOver ? songs.length : stage + 1}</span> / {songs.length}
            </div>
            <div className="text-green-600 font-bold text-lg">
              Score: <span className="text-green-900">{score}</span>
            </div>
          </div>
          {!songs.length && (
            <div className="text-center text-gray-500">Loading songs...</div>
          )}
          {!gameOver && songs.length > 0 && songs[stage] && (
            <>
              <div className="flex flex-col items-center mb-4">
                <div className="relative w-full flex items-center justify-center">
                  <button
                    className={`rounded-full bg-gradient-to-r from-green-700 to-lime-400 shadow px-8 py-4 text-white text-lg font-bold mb-3 hover:scale-105 transition
                      ${isPlaying ? "opacity-70 pointer-events-none" : "hover:bg-green-800"}`}
                    onClick={handlePlayPreview}
                    disabled={isPlaying}
                  >
                    ▶️ Play 45s Preview
                  </button>
                  <audio
                    ref={audioRef}
                    src={songs[stage].audio_url}
                    preload="auto"
                    onPause={() => setIsPlaying(false)}
                  />
                </div>
                <div className="text-gray-400 text-xs mb-2">
                  {isPlaying ? `Playing... (${Math.min(45, Math.floor(audioTime))} / 45s)` : "Click play to listen"}
                </div>
              </div>
              <form onSubmit={handleGuess} className="flex flex-col items-center gap-2 w-full">
                <div className="w-full flex flex-col gap-2">
                  {options.map((title, i) => (
                    <button
                      key={i}
                      type="button"
                      disabled={showResult}
                      onClick={() => setUserGuess(title)}
                      className={`w-full text-left px-4 py-2 rounded-xl border border-green-200 font-bold transition shadow-sm focus:outline-none
                        ${userGuess === title
                          ? "bg-lime-200 border-lime-400 text-green-900"
                          : "bg-white hover:bg-lime-100"}
                        ${showResult && title === songs[stage].title
                          ? "border-2 border-lime-600 bg-lime-100"
                          : ""}
                        `}
                    >
                      {title}
                    </button>
                  ))}
                </div>
                <button
                  type="submit"
                  className="w-full max-w-xs bg-gradient-to-r from-green-700 to-lime-400 text-white py-2 rounded-xl font-bold text-lg shadow hover:from-green-800 hover:to-lime-600 transition mt-2"
                  disabled={!userGuess || showResult}
                >
                  Submit Answer
                </button>
              </form>
              {showResult && (
                <div className="mt-4 text-lg font-bold">
                  {userGuess.trim().toLowerCase() === songs[stage].title.trim().toLowerCase() ? (
                    <span className="text-lime-600">🎉 Correct!</span>
                  ) : (
                    <span className="text-rose-600">❌ Oops! The answer was: <b>{songs[stage].title}</b></span>
                  )}
                </div>
              )}
            </>
          )}

          {/* Game Over Screen */}
          {gameOver && (
            <div className="flex flex-col items-center justify-center py-8">
              {score === songs.length && (
                <div className="relative flex flex-col items-center mb-4">
                  {/* Champagne pop effect */}
                  <div className="animate-pop scale-150 mb-2 relative">
                    <span className="text-5xl" role="img" aria-label="champagne">🍾</span>
                    {/* "Confetti" style using emojis */}
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center pointer-events-none select-none">
                      <div className="flex gap-2 animate-bounce">
                        <span className="text-2xl">🎉</span>
                        <span className="text-2xl">✨</span>
                        <span className="text-2xl">🎊</span>
                      </div>
                      <div className="flex gap-1 mt-1 animate-pulse">
                        <span className="text-lg">🥂</span>
                        <span className="text-lg">🎶</span>
                        <span className="text-lg">🌟</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-3xl text-lime-700 font-extrabold mb-1 animate-bounce">
                    Congratulations!
                  </div>
                  <div className="text-green-900 font-bold text-xl mb-2">You scored 100%!</div>
                  <div className="text-green-700 text-base mb-2 text-center max-w-md">
                    You’re a true Choir Center Game Master. Share your achievement:
                  </div>
                  <div className="flex gap-3 mt-2">
                    <a
                      href={fbShare}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 py-2 shadow flex items-center gap-2 text-sm font-semibold"
                    >
                      <span className="text-lg">📘</span> Facebook
                    </a>
                    <a
                      href={waShare}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-green-500 hover:bg-green-600 text-white rounded-full px-4 py-2 shadow flex items-center gap-2 text-sm font-semibold"
                    >
                      <span className="text-lg">🟢</span> WhatsApp
                    </a>
                    <a
                      href={xShare}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-black hover:bg-gray-800 text-white rounded-full px-4 py-2 shadow flex items-center gap-2 text-sm font-semibold"
                    >
                      <span className="text-lg">𝕏</span> X
                    </a>
                  </div>
                </div>
              )}
              {score !== songs.length && (
                <>
                  <div className="text-3xl text-green-700 font-extrabold mb-2">🏆 Game Over!</div>
                  <div className="text-xl mb-3 text-green-900 font-bold">Your final score: <span className="text-lime-600">{score} / {songs.length}</span></div>
                  <div className="mb-6 text-green-700 font-medium">Thank you for playing! Want to try again?</div>
                </>
              )}
              <button
                className="bg-gradient-to-r from-green-700 to-lime-400 text-white px-6 py-3 rounded-xl font-bold text-lg shadow hover:from-green-800 hover:to-lime-600 transition mt-2"
                onClick={handleRestart}
              >
                Play Again
              </button>
            </div>
          )}
        </div>
        <div className="w-full mt-8 text-center text-green-500 text-sm italic">
          ChoirCenter.com • Choir Song Guessing Game
        </div>
      </div>

      {/* Tailwind custom animation */}
      <style>
        {`
          @keyframes pop {
            0% { transform: scale(0.6); opacity: 0.2; }
            60% { transform: scale(1.3); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
          .animate-pop {
            animation: pop 0.8s cubic-bezier(0.4,0.8,0.5,1.2) 1;
          }
        `}
      </style>
    </div>
  );
}
